/**
 * 설정 값을 로드하고 필요한 형식으로 변환합니다.
 * 우선순위: S3 설정 > 환경 변수 > 로컬 설정(config.js) > 기본 설정(DEFAULT_CONFIG)
 */
import config from './config';
import { DEFAULT_CONFIG } from './config.default';
import { formatUTCtoEnglishDate } from '../utils/dateUtils';
import axios from 'axios';

// 설정은 config.js에서 로드한 값으로 초기화합니다
let cachedConfig = {
  ...config,
  APPLICATION_CLOSE_DATE: new Date(config.APPLICATION_CLOSE_DATE || DEFAULT_CONFIG.APPLICATION_CLOSE_DATE),
  APPLICATION_START_DATE: new Date(config.APPLICATION_START_DATE || DEFAULT_CONFIG.APPLICATION_START_DATE),
  PRESENTATION_PERIOD: {
    strStartDate: formatUTCtoEnglishDate(config.APPLICATION_START_DATE || DEFAULT_CONFIG.APPLICATION_START_DATE),
    strEndDate: formatUTCtoEnglishDate(config.APPLICATION_CLOSE_DATE || DEFAULT_CONFIG.APPLICATION_CLOSE_DATE)
  }
};

// S3에서 설정을 로드했는지 여부와 마지막 로드 시간을 추적
let isConfigLoaded = false;
let lastConfigLoadTime = 0;
const CONFIG_LOAD_THRESHOLD = 60000; // 설정 재로드 허용 시간 (ms) - 1분

// 내보낸 설정값 변수들 - 초기에는 config.js 값으로 설정
export let APPLICATION_CLOSE_DATE = cachedConfig.APPLICATION_CLOSE_DATE;
export let APPLICATION_START_DATE = cachedConfig.APPLICATION_START_DATE;
export let PRESENTATION_PERIOD = cachedConfig.PRESENTATION_PERIOD;

// 설정이 업데이트될 때 내보낸 변수들도 업데이트
const updateExportedConfig = () => {
  APPLICATION_CLOSE_DATE = cachedConfig.APPLICATION_CLOSE_DATE;
  APPLICATION_START_DATE = cachedConfig.APPLICATION_START_DATE;
  PRESENTATION_PERIOD = cachedConfig.PRESENTATION_PERIOD;
  
  console.log('📊 현재 적용된 설정:', {
    APPLICATION_START_DATE: APPLICATION_START_DATE.toISOString(),
    APPLICATION_CLOSE_DATE: APPLICATION_CLOSE_DATE.toISOString(),
    PRESENTATION_PERIOD
  });
};

// 초기 설정 업데이트 
updateExportedConfig();

/**
 * S3 URL에서 설정 파일을 로드합니다.
 * @param {string} url - S3 버킷의 config.json URL
 * @param {boolean} forceReload - 강제로 캐시를 무시하고 재로드할지 여부
 * @returns {Promise<Object>} 로드된 설정 객체
 */
export const loadConfigFromS3 = async (url, forceReload = false) => {
  if (!url) {
    console.log('⚠️ S3 URL이 제공되지 않았습니다. 로컬 설정을 계속 사용합니다.');
    return cachedConfig;
  }
  
  // 현재 시간
  const now = Date.now();
  
  // 이미 로드되었고 마지막 로드 이후 일정 시간이 지나지 않았다면 캐시된 설정을 반환
  if (isConfigLoaded && !forceReload && (now - lastConfigLoadTime < CONFIG_LOAD_THRESHOLD)) {
    console.log('⏱️ 최근에 S3 설정을 로드했습니다. 캐시된 설정을 사용합니다.');
    return cachedConfig;
  }
  
  console.log('🔵 S3 설정 로드 시작 ==============================');
  console.log('📍 요청 URL:', url);
  console.log('📍 현재 시간:', new Date().toISOString());
  
  try {
    // S3에서 설정 로드 시도
    console.log('📡 S3에서 설정 불러오기 시도 중...');
    
    const response = await axios.get(url, {
      withCredentials: false,
      timeout: 10000
    });
    
    console.log('📊 S3 응답 상태:', response.status, response.statusText);
    
    if (response.status !== 200) {
      throw new Error(`S3에서 설정 불러오기 실패: ${response.status} ${response.statusText}`);
    }
    
    const newConfig = response.data;
    console.log('📥 S3에서 받은 설정 데이터(파싱 성공):', JSON.stringify(newConfig, null, 2));
    
    // 설정값 캐싱 및 형식 변환 (S3 설정이 최우선)
    cachedConfig = {
      ...cachedConfig, // 기존 설정 유지 (최하위 우선순위)
      ...newConfig,    // S3 설정 덮어쓰기 (최우선)
      APPLICATION_CLOSE_DATE: new Date(newConfig.APPLICATION_CLOSE_DATE),
      APPLICATION_START_DATE: new Date(newConfig.APPLICATION_START_DATE),
      // PRESENTATION_PERIOD 생성 (날짜 형식 변환)
      PRESENTATION_PERIOD: {
        strStartDate: formatUTCtoEnglishDate(newConfig.APPLICATION_START_DATE),
        strEndDate: formatUTCtoEnglishDate(newConfig.APPLICATION_CLOSE_DATE)
      }
    };
    
    // 설정 로드 상태 업데이트
    isConfigLoaded = true;
    lastConfigLoadTime = now;
    
    console.log('✅ S3 설정을 성공적으로 불러와 적용했습니다');
    console.log('🔵 S3 설정 로드 종료 (성공) ==============================');
    
    // 설정값 갱신된 객체 내보내기
    updateExportedConfig();
    
    return cachedConfig;
  } catch (error) {
    console.error('❌ S3에서 설정 불러오기 실패:', error);
    console.log('📌 S3 설정 로드 실패로 기존 설정을 계속 사용합니다');
    console.log('🔵 S3 설정 로드 종료 (실패) ==============================');
    
    // 하드코딩된 기본값 사용 (S3 로드 실패 시)
    const hardcodedConfig = {
      "APPLICATION_START_DATE": "2025-04-14T15:00:00Z",
      "APPLICATION_CLOSE_DATE": "2025-06-06T14:59:59Z"
    };
    
    console.log('📥 S3 로드 실패로 하드코딩된 설정 데이터 사용:', JSON.stringify(hardcodedConfig, null, 2));
    
    // 하드코딩된 설정값 적용
    cachedConfig = {
      ...cachedConfig,
      ...hardcodedConfig,
      APPLICATION_CLOSE_DATE: new Date(hardcodedConfig.APPLICATION_CLOSE_DATE),
      APPLICATION_START_DATE: new Date(hardcodedConfig.APPLICATION_START_DATE),
      PRESENTATION_PERIOD: {
        strStartDate: formatUTCtoEnglishDate(hardcodedConfig.APPLICATION_START_DATE),
        strEndDate: formatUTCtoEnglishDate(hardcodedConfig.APPLICATION_CLOSE_DATE)
      }
    };
    
    // 실패해도 설정을 로드한 것으로 간주하고 캐싱 처리
    isConfigLoaded = true;
    lastConfigLoadTime = now;
    
    // 설정값 갱신된 객체 내보내기
    updateExportedConfig();
    
    return cachedConfig;
  }
};

/**
 * 현재 캐시된 설정을 반환합니다.
 * @returns {Object} 캐시된 설정 객체
 */
export const getCurrentConfig = () => {
  return cachedConfig;
};

export default {
  get APPLICATION_CLOSE_DATE() { return APPLICATION_CLOSE_DATE; },
  get APPLICATION_START_DATE() { return APPLICATION_START_DATE; },
  get PRESENTATION_PERIOD() { return PRESENTATION_PERIOD; },
  loadConfigFromS3,
  getCurrentConfig
}; 